import { ArrowLeftOutlined, FileDoneOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, DatePicker, Drawer, Form, Input, Radio, Select, Switch, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { apiErrorMessage } from '../api/client';
import { categoriesApi, operationsApi, skladsApi, storesApi } from '../api/endpoints';
import { invalidateMoneyData, queryKeys } from '../api/queryKeys';
import { OPERATION_TYPES as ALL_TYPES, PAYMENT_METHODS } from '../api/types';
import type { CreateOperationBody, OperationType, PaymentMethod } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { useStoreContext } from '../context/StoreContext';
import { formatKopeks, rubToKopeks } from '../lib/money';
import { TYPE_FIELDS } from '../lib/operationShape';
import { AmountInput, positiveAmountRule } from './AmountInput';
import { ModalSelect } from './ModalSelect';
import { OP_TYPE_ICON } from './OpTypeTag';

const DATE_FORMAT = 'DD.MM.YYYY';

// Приход/выдача are created ONLY through a накладная (with product lines) now,
// so the quick drawer offers just these single operations.
const SINGLE_TYPES: OperationType[] = ALL_TYPES.filter(
  (type) => type !== 'PURCHASE' && type !== 'ISSUE',
);

interface SingleFormValues {
  storeId?: string;
  skladId?: string;
  categoryId?: string;
  paymentMethod?: PaymentMethod;
  deductFromStoreBalance?: boolean;
  amount: string;
  date: Dayjs;
  comment?: string;
}

/**
 * FAB-opened bottom drawer (SPEC §5). The featured card is the combined
 * «Приход + Выдача» flow; below it, one button per single operation type.
 * After a successful submit the form resets and the drawer STAYS OPEN so the
 * owner can chain entries — a repeat combined entry is ≤5 taps.
 */
export function QuickEntryDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { storeId: ctxStoreId, storeIds: ctxStoreIds, scopeStores } = useStoreContext();
  const { isStoreRestricted } = useAuth();
  // A store-restricted USER cannot book whole-business rows: no sklad
  // payments, and every other entry must name one of their stores.
  const singleTypes = isStoreRestricted
    ? SINGLE_TYPES.filter((type) => type !== 'SKLAD_PAYMENT')
    : SINGLE_TYPES;
  const [view, setView] = useState<'main' | OperationType>('main');
  const [singleForm] = Form.useForm<SingleFormValues>();
  // «Вычесть из остатка магазина» needs a store, so it makes the store field
  // required while it is on (no-op when the store comes from the global context).
  const deductOn = Form.useWatch('deductFromStoreBalance', singleForm);

  const skladsQuery = useQuery({
    queryKey: queryKeys.sklads(false),
    queryFn: () => skladsApi.list(false),
    enabled: open,
  });
  const categoriesQuery = useQuery({
    queryKey: queryKeys.categories(false),
    queryFn: () => categoriesApi.list(false),
    enabled: open,
  });

  const storeOptions = scopeStores.map((s) => ({ value: s.id, label: s.name }));
  const skladOptions = (skladsQuery.data ?? []).map((s) => ({ value: s.id, label: s.name }));
  const categoryOptions = (categoriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  const resetDates = (): void => {
    singleForm.setFieldsValue({ date: dayjs() });
  };

  const openManualShipment = (): void => {
    onClose();
    navigate('/shipments/new');
  };

  const singleMutation = useMutation({
    mutationFn: async (input: { type: OperationType; values: SingleFormValues }) => {
      const { type, values } = input;
      const fields = TYPE_FIELDS[type];
      const storeId =
        fields.store === 'none' ? undefined : (ctxStoreId ?? values.storeId) || undefined;
      const body: CreateOperationBody = {
        type,
        date: values.date.format('YYYY-MM-DD'),
        amount: rubToKopeks(values.amount),
        ...(storeId ? { storeId } : {}),
        ...(fields.sklad ? { skladId: values.skladId } : {}),
        ...(fields.category ? { categoryId: values.categoryId } : {}),
        // Backend requires the method on new store payments; CASH is the
        // pre-selected default, so this is always present.
        ...(type === 'STORE_PAYMENT' ? { paymentMethod: values.paymentMethod ?? 'CASH' } : {}),
        // EXPENSE «вычесть из остатка магазина»: only sent when on AND a store is
        // resolved (the switch's rule makes the store required when on).
        ...(type === 'EXPENSE' && values.deductFromStoreBalance && storeId
          ? { deductFromStoreBalance: true }
          : {}),
        ...(values.comment?.trim() ? { comment: values.comment.trim() } : {}),
      };
      const operation = await operationsApi.create(body);
      // Success toast shows the NEW balance (SPEC §5) — POST /operations
      // returns only the row, so fetch the fresh directory list.
      if (type === 'ISSUE' || type === 'STORE_PAYMENT') {
        const freshStores = await storesApi.list(true);
        const store = freshStores.find((s) => s.id === operation.storeId);
        return store
          ? t('quick.storeBalanceSuccess', { name: store.name, balance: formatKopeks(store.balance) })
          : t('common.saved');
      }
      if (type === 'PURCHASE' || type === 'SKLAD_PAYMENT') {
        const freshSklads = await skladsApi.list(true);
        const sklad = freshSklads.find((s) => s.id === operation.skladId);
        return sklad
          ? t('quick.skladDebtSuccess', { name: sklad.name, balance: formatKopeks(sklad.debt) })
          : t('common.saved');
      }
      return t('quick.expenseSuccess');
    },
    onSuccess: (successText) => {
      message.success(successText, 5);
      invalidateMoneyData(queryClient);
      singleForm.resetFields();
      resetDates();
    },
    onError: (error) => {
      message.error(apiErrorMessage(error) ?? t('common.requestError'));
    },
  });

  const storeField = (mode: 'required' | 'optional'): JSX.Element | null => {
    // Store chosen in the global context → pre-filled and hidden (SPEC §5).
    if (ctxStoreId) return null;
    return (
      <Form.Item
        name="storeId"
        label={mode === 'required' ? t('common.store') : t('quick.storeOptional')}
        rules={mode === 'required' ? [{ required: true, message: t('common.storeRequired') }] : []}
      >
        <ModalSelect
          size="large"
          placeholder={t('quick.storePlaceholder')}
          title={t('common.store')}
          options={storeOptions}
          allowClear={mode === 'optional'}
        />
      </Form.Item>
    );
  };

  const dateField = (
    <Form.Item name="date" label={t('common.date')} rules={[{ required: true, message: t('common.dateRequired') }]}>
      <DatePicker
        size="large"
        style={{ width: '100%' }}
        format={DATE_FORMAT}
        allowClear={false}
        inputReadOnly
        // Body-mounted (overrides the app-wide getPopupContainer): inside the
        // drawer's clipped container the panel can open past the viewport top,
        // hiding the month/year header on short mobile screens.
        getPopupContainer={() => document.body}
      />
    </Form.Item>
  );

  const commentField = (
    <Form.Item name="comment" label={t('common.comment')}>
      <Input size="large" placeholder={t('common.commentPlaceholder')} autoComplete="off" maxLength={1000} />
    </Form.Item>
  );

  const mainView = (
    <>
      {/* Приход + Выдача is now always a накладная (with product lines) — this
          opens the manual накладная editor instead of a bare amount form. */}
      <Button
        type="primary"
        size="large"
        icon={<FileDoneOutlined />}
        onClick={openManualShipment}
        style={{ height: 56, justifyContent: 'center', fontSize: 16 }}
        block
      >
        {t('shipments.createManual')}
      </Button>
      <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 16 }}>
        {t('quick.combinedHint')}
      </Typography.Paragraph>

      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        {t('quick.singleTitle')}
      </Typography.Text>
      <div style={{ display: 'grid', gap: 8 }}>
        {singleTypes.map((type) => (
          <Button
            key={type}
            size="large"
            icon={OP_TYPE_ICON[type]}
            style={{ justifyContent: 'flex-start', height: 48 }}
            onClick={() => {
              singleForm.resetFields();
              singleForm.setFieldsValue({ date: dayjs() });
              setView(type);
            }}
            block
          >
            {t(`opType.${type}`)}
          </Button>
        ))}
      </div>
    </>
  );

  const singleView = (type: OperationType): JSX.Element => {
    const fields = TYPE_FIELDS[type];
    return (
      <Form<SingleFormValues>
        form={singleForm}
        layout="vertical"
        requiredMark={false}
        initialValues={{ date: dayjs() }}
        onFinish={(values) => singleMutation.mutate({ type, values })}
      >
        {fields.sklad ? (
          <Form.Item
            name="skladId"
            label={t('common.sklad')}
            rules={[{ required: true, message: t('common.skladRequired') }]}
          >
            <ModalSelect
              size="large"
              placeholder={t('quick.skladPlaceholder')}
              title={t('common.sklad')}
              options={skladOptions}
              loading={skladsQuery.isLoading}
            />
          </Form.Item>
        ) : null}
        {fields.category ? (
          <Form.Item
            name="categoryId"
            label={t('common.category')}
            rules={[{ required: true, message: t('common.categoryRequired') }]}
          >
            <Select
              size="large"
              placeholder={t('quick.categoryPlaceholder')}
              options={categoryOptions}
              loading={categoriesQuery.isLoading}
            />
          </Form.Item>
        ) : null}
        {fields.store !== 'none'
          ? storeField(
              (type === 'EXPENSE' && deductOn) || isStoreRestricted || ctxStoreIds
                ? 'required'
                : fields.store,
            )
          : null}
        {type === 'EXPENSE' ? (
          <Form.Item
            name="deductFromStoreBalance"
            label={t('quick.deductFromStore')}
            tooltip={t('quick.deductFromStoreHint')}
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
        ) : null}
        <Form.Item name="amount" label={t('common.amount')} rules={[positiveAmountRule(t)]}>
          <AmountInput />
        </Form.Item>
        {type === 'STORE_PAYMENT' ? (
          <Form.Item
            name="paymentMethod"
            label={t('payMethod.label')}
            initialValue="CASH"
            rules={[{ required: true, message: t('payMethod.required') }]}
          >
            <Radio.Group
              size="large"
              optionType="button"
              buttonStyle="solid"
              style={{ width: '100%', display: 'flex' }}
              options={PAYMENT_METHODS.map((method) => ({
                value: method,
                label: t(`payMethod.${method}`),
                style: { flex: 1, textAlign: 'center' as const },
              }))}
            />
          </Form.Item>
        ) : null}
        {dateField}
        {commentField}
        <Button type="primary" htmlType="submit" size="large" block loading={singleMutation.isPending}>
          {t('common.create')}
        </Button>
      </Form>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={() => {
        setView('main');
        onClose();
      }}
      placement="bottom"
      height="88%"
      title={
        view === 'main' ? (
          t('quick.title')
        ) : (
          <span>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => setView('main')}
              style={{ marginInlineEnd: 8 }}
              aria-label={t('common.back')}
            />
            {t(`opType.${view}`)}
          </span>
        )
      }
      styles={{ body: { padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' } }}
    >
      {view === 'main' ? mainView : singleView(view)}
    </Drawer>
  );
}
